import { getAnonymousId } from '@shipt/analytics-member-web'
import { useHydrated } from '@/features/shared/components/ClientOnly'
import {
  BRANCH_ACTION_PROMOTION_PAGE,
  BRANCH_ACTION_LANDING_PAGE,
  BRANCH_ACTION_PDP_PAGE,
  BRANCH_ACTION_MANUAL_RESCHEDULE,
  BRANCH_ACTION_MANUAL_RESCHEDULE_ORDER,
  BRANCH_ACTION_ORDER_PROGRESS,
  BRANCH_ACTION_GO_TO_SEARCH,
  BRANCH_ACTION_SEARCH_PRODUCTS,
  BRANCH_ACTION_GO_TO_SEARCH_QUERY,
} from '@/features/shared/constants/branch'
import {
  routes,
  getRetailerUrl,
  getRetailerSearchUrl,
  getRetailerFeaturedPromotionsUrl,
  getRetailerLandingPageUrl,
} from '@shared/constants/routes'
import { type BranchLinkData } from '@/lib/branch/types'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { isOnServer } from '@shared/constants/util'

export const getDeepLinkRedirectRoute = (
  linkData: BranchLinkData,
  storeName: string | undefined,
): string => {
  const {
    query,
    action,
    promotion_id = 0,
    order_id,
    landingPageType,
    landingPageSlug,
    product_id = 0,
    shop_progress,
    category_id,
  } = linkData

  const searchWithCategoryId = `${getRetailerSearchUrl(storeName)}?category_id=${category_id}`

  const redirectRoute: Record<string, string> = {
    [BRANCH_ACTION_PDP_PAGE]: getProductPageUrl({
      id: product_id,
      universal_id: undefined,
    }),
    [BRANCH_ACTION_LANDING_PAGE]: `${getRetailerLandingPageUrl(
      storeName,
    )}/${landingPageType}/${landingPageSlug}`,
    [BRANCH_ACTION_MANUAL_RESCHEDULE]: `${routes.ORDER_DETAIL.url}/${order_id}?action=reschedule_order`,
    [BRANCH_ACTION_MANUAL_RESCHEDULE_ORDER]: `${routes.ORDER_DETAIL.url}/${order_id}?action=reschedule_order`,
    [BRANCH_ACTION_PROMOTION_PAGE]: `${getRetailerFeaturedPromotionsUrl(
      storeName,
      promotion_id,
    )}?promotion_category_id=${promotion_id}`,
    [BRANCH_ACTION_ORDER_PROGRESS]: `${
      routes.ORDER_DETAIL.url
    }/${order_id}?shop_progress=${shop_progress ?? 'found'}`,
    [BRANCH_ACTION_GO_TO_SEARCH]: searchWithCategoryId,
    [BRANCH_ACTION_SEARCH_PRODUCTS]: searchWithCategoryId,
    [BRANCH_ACTION_GO_TO_SEARCH_QUERY]: `${getRetailerSearchUrl(storeName)}?query=${query}`,
  }
  return redirectRoute[action] ?? getRetailerUrl(storeName)
}

const BRANCH_APP_LINK_HOSTNAME = 'app.link'

/**
 * Appends required Branch.io deep link tracking params to any `*.app.link` URL.
 * Non-Branch URLs (and any unparseable URLs) are returned unchanged.
 * After hydration, returns the URL with `web_anonymous_id` appended, done by useAppendBranchDeepLinkParams hook
 */
const appendBranchDeepLinkParams = (url: string): string => {
  if (!url) return url

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }

  const isBranchAppLinkHost =
    parsed.hostname === BRANCH_APP_LINK_HOSTNAME ||
    parsed.hostname.endsWith(`.${BRANCH_APP_LINK_HOSTNAME}`)

  if (!isBranchAppLinkHost) {
    return url
  }

  const anonymousId = !isOnServer() ? getAnonymousId() : undefined

  if (anonymousId) {
    parsed.searchParams.set('web_anonymous_id', anonymousId)
  }

  return parsed.toString()
}

export const useAppendBranchDeepLinkParams = (url: string): string => {
  const hydrated = useHydrated()
  return hydrated ? appendBranchDeepLinkParams(url) : url
}
